import { useRef, useState, type ReactElement } from "react";
import type { PeopleSource } from "@playbookai/types";
import { FormField } from "../molecules/FormField/FormField.js";
import { PeopleField } from "../molecules/PeopleField/PeopleField.js";
import type {
  PeopleFieldGrantTiming,
  PeopleFieldValue,
} from "../molecules/PeopleField/people-field.types.js";
import type { WidgetChangeOptions } from "./types.js";

/**
 * A widget person field as it is stored (assign-person d21): the text a reader
 * sees and an optional member link, which only a pick sets.
 */
export interface WidgetPerson {
  readonly text: string;
  readonly userId: string | null;
}

/** The stored pair as PeopleField's value: linked, a typed name, or nobody. */
export function personFieldValue(
  text: string,
  userId: string | null | undefined,
): PeopleFieldValue {
  if (userId) return { kind: "person", id: userId, name: text };
  return text.trim() ? { kind: "typed", name: text } : null;
}

/** PeopleField's value as the stored pair. A typed name carries no link (d29). */
export function widgetPerson(value: PeopleFieldValue): WidgetPerson {
  if (value === null) return { text: "", userId: null };
  return value.kind === "person"
    ? { text: value.name, userId: value.id }
    : { text: value.name, userId: null };
}

/** Only a pick of a real person may add them as Viewer (d25). */
export function personChangeOptions(
  value: PeopleFieldValue,
): WidgetChangeOptions | undefined {
  return value?.kind === "person" ? { grantViewerIfMissing: true } : undefined;
}

/**
 * The shared person field for a widget's own field (Owner, Responsible,
 * Stakeholder), on the canvas's `PeopleSource`. Individual fields can require
 * an account pick while preserving existing names for display.
 *
 * When `onChange` returns a promise — an instant field saving a pick — a
 * refusal shows its message under the field and the pick stays on screen, so
 * the person can see what failed and choose again. A form passes its own
 * Save's refusal through `error` instead.
 */
export function WidgetPersonField({
  source,
  label,
  text,
  userId,
  onChange,
  timing = "now",
  pendingGrant = false,
  error,
  required = false,
  readOnly = false,
  allowTyped = true,
  onFocus,
  onBlur,
}: {
  readonly source: PeopleSource;
  readonly label: string;
  readonly text: string;
  readonly userId: string | null | undefined;
  readonly onChange: (
    next: WidgetPerson,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  readonly timing?: PeopleFieldGrantTiming;
  /** The value is a pick a form will save, so its person may get access. */
  readonly pendingGrant?: boolean;
  readonly error?: string | null | undefined;
  readonly required?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly allowTyped?: boolean;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
}): ReactElement {
  const [failed, setFailed] = useState<{
    readonly value: PeopleFieldValue;
    readonly message: string;
  } | null>(null);
  // Only the newest pick may report a failure: a slow older pick that fails
  // after a newer one saved must not put its stale person back on screen.
  const latestPick = useRef(0);
  const change = (next: PeopleFieldValue): void => {
    setFailed(null);
    const pick = ++latestPick.current;
    const saved = onChange(widgetPerson(next), personChangeOptions(next));
    if (!(saved instanceof Promise)) return;
    saved.catch((reason: unknown) => {
      if (pick !== latestPick.current) return;
      setFailed({
        value: next,
        message:
          reason instanceof Error && reason.message
            ? reason.message
            : "Edit not saved",
      });
    });
  };
  const message = failed?.message ?? error ?? undefined;
  return (
    <FormField
      label={label}
      required={required}
      {...(message ? { error: message } : {})}
    >
      <PeopleField
        value={failed ? failed.value : personFieldValue(text, userId)}
        onChange={change}
        source={source}
        label={label}
        allowTyped={allowTyped}
        grantTiming={timing}
        pendingGrant={pendingGrant}
        readOnly={readOnly}
        {...(onFocus ? { onFocus } : {})}
        {...(onBlur ? { onBlur } : {})}
      />
    </FormField>
  );
}
